import { findIpv4Metadata } from '../data/ipAddressCatalog';

// Design agent: Provides IPv4 parsing, formatting, and computation helpers used across calculators.
const IPV4_REGEX = /^(?:\d{1,3}\.){3}\d{1,3}$/;

// Design agent: Converts dotted decimal IPv4 string to a 32-bit number.
export function ipv4ToInt(ip) {
  const parts = ip.split('.').map(Number);
  if (parts.length !== 4 || parts.some((part) => Number.isNaN(part) || part < 0 || part > 255)) {
    return null;
  }
  return (
    (parts[0] << 24) |
    (parts[1] << 16) |
    (parts[2] << 8) |
    parts[3]
  ) >>> 0;
}

// Design agent: Converts 32-bit number back to dotted decimal IPv4 string.
export function intToIpv4(intValue) {
  return [
    (intValue >>> 24) & 0xff,
    (intValue >>> 16) & 0xff,
    (intValue >>> 8) & 0xff,
    intValue & 0xff,
  ].join('.');
}

// Design agent: Parses a CIDR string like 192.168.0.0/24 into structured data.
export function parseCidr(input) {
  if (typeof input !== 'string') {
    return null;
  }
  const trimmed = input.trim();
  const [ipPart, prefixPart] = trimmed.split('/');
  if (!IPV4_REGEX.test(ipPart)) {
    return null;
  }
  const prefix = Number(prefixPart);
  if (Number.isNaN(prefix) || prefix < 0 || prefix > 32) {
    return null;
  }
  const baseIp = ipv4ToInt(ipPart);
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
  // Design agent: Ensure network arithmetic stays unsigned to avoid negative overflow.
  const network = (baseIp & mask) >>> 0;
  const broadcast = (network | (~mask >>> 0)) >>> 0;
  return {
    ip: ipPart,
    prefix,
    mask,
    network,
    broadcast,
  };
}

// Design agent: Formats a mask integer into dotted decimal notation.
export function formatMask(maskInt) {
  return intToIpv4(maskInt >>> 0);
}

// Design agent: Produces wildcard mask (inverse of netmask).
export function formatWildcard(maskInt) {
  const wildcard = (~maskInt) >>> 0;
  return formatMask(wildcard);
}

// Design agent: Determines the IPv4 class based on the first octet.
export function getIpv4Class(ip) {
  const parts = ip.split('.').map(Number);
  const first = parts[0];
  if (first <= 127) return 'Class A';
  if (first <= 191) return 'Class B';
  if (first <= 223) return 'Class C';
  if (first <= 239) return 'Class D (Multicast)';
  return 'Class E (Experimental)';
}

// Design agent: Finds the smallest common prefix that covers two integer IPs.
export function commonPrefixLength(a, b) {
  let xor = (a ^ b) >>> 0;
  let prefix = 32;
  while (xor > 0) {
    xor >>>= 1;
    prefix -= 1;
  }
  return prefix;
}

// Design agent: Aligns an IP integer to the next multiple of a block size within a base network.
export function alignToBlock(address, blockSize, baseNetwork) {
  const offset = address - baseNetwork;
  const alignedOffset = Math.ceil(offset / blockSize) * blockSize;
  return baseNetwork + alignedOffset;
}

// Design agent: Checks if string looks like IPv4 subnet (CIDR).
export function isValidCidr(input) {
  return Boolean(parseCidr(input));
}

// Design agent: Retrieves structured metadata for an IPv4 integer.
export function getAddressMetadata(ipInt) {
  return findIpv4Metadata(ipInt);
}

// Design agent: Preserves backward compatibility by exposing only the label.
export function getAddressType(ipInt) {
  const metadata = getAddressMetadata(ipInt);
  return metadata.label;
}

// Design agent: Calculates the usable host count for a prefix, handling edge cases like /31 and /32.
export function getUsableHostCount(prefix) {
  if (prefix === 31) return 2;
  if (prefix === 32) return 1;
  const hostBits = 32 - prefix;
  return Math.max(0, 2 ** hostBits - 2);
}

// Design agent: Calculates total address count for a prefix.
export function getTotalAddressCount(prefix) {
  const hostBits = 32 - prefix;
  return 2 ** hostBits;
}

// Developer agent: Determines if two network ranges overlap.
// Design agent: Provides a boolean check for visual overlap indicators.
export function doNetworksOverlap(networkA, networkB) {
  return (
    (networkA.network <= networkB.broadcast && networkA.broadcast >= networkB.network)
  );
}

// Design agent: Spec 002 — centralises the VLSM sizing rule so the same formula backs both the
// allocation loop and its unit tests. Point-to-point links (RFC 3021) only make sense for 1 or 2
// hosts: a /32 has no room for a peer, so both 1-host and 2-host requests collapse to the same
// two-address /31 block. From 3 hosts up, the historical "hosts + 2 rounded up to a power of two"
// formula is unchanged, whatever the toggle is set to.
// Developer agent: Returns a CIDR prefix (0-32) for a requested host count. `allowPointToPoint`
// mirrors the VLSM method 1 toggle; when false the function always falls back to the legacy formula.
export function computeSubnetPrefixForHosts(hosts, allowPointToPoint) {
  const safeHosts = Number.isFinite(hosts) ? hosts : 0;

  if (allowPointToPoint && safeHosts <= 2) {
    return 31;
  }

  const neededAddresses = safeHosts <= 0 ? 2 : safeHosts + 2;
  const blockSize = 2 ** Math.ceil(Math.log2(Math.max(neededAddresses, 2)));
  return 32 - Math.log2(blockSize);
}

// Design agent: Spec 007 — finds the largest power-of-two block size that both (a) fits within the
// remaining free space and (b) starts on a boundary its own size divides evenly, which is the CIDR
// alignment rule (a /26 network address must be a multiple of 64, etc). Returns Infinity for a
// start of 0 so the very first block is limited only by how much space remains.
// Developer agent: Uses plain division/modulo (never bitwise `&`/`~`) because addresses can exceed
// 2^31 and JavaScript's bitwise operators coerce to signed 32-bit integers, which would corrupt the
// upper half of the IPv4 space.
function largestAlignedBlockSize(address) {
  if (address === 0) {
    return Infinity;
  }
  let size = 1;
  while (address % (size * 2) === 0) {
    size *= 2;
  }
  return size;
}

// Design agent: Spec 007 — turns one contiguous stretch of free addresses into the fewest possible
// aligned CIDR blocks (a classic "greedy largest block first" range-to-CIDR decomposition), so the
// VLSM pool usage panel can tell an operator exactly what still fits ("1 × /25, 2 × /26, ...").
// Developer agent: `start` is inclusive, `end` is exclusive. Both must already be non-negative
// 32-bit address integers; the caller (VlsmCalculator) is responsible for passing an aligned start
// (every gap it records starts on a boundary because it comes from `alignToBlock`).
export function decomposeFreeSpace(start, end) {
  const blocks = [];
  let cursor = start;

  while (cursor < end) {
    const remainingSpace = end - cursor;
    let largestFittingSize = 1;
    while (largestFittingSize * 2 <= remainingSpace) {
      largestFittingSize *= 2;
    }
    const blockSize = Math.min(largestFittingSize, largestAlignedBlockSize(cursor));
    const prefix = 32 - Math.log2(blockSize);
    blocks.push({ network: cursor, prefix, blockSize });
    cursor += blockSize;
  }

  return blocks;
}

// Developer agent: Calculates the number of addresses in the overlapping region of two networks.
// Design agent: Quantifies the shared address space for overlap metrics.
export function getOverlapSize(networkA, networkB) {
  const overlapStart = Math.max(networkA.network, networkB.network);
  const overlapEnd = Math.min(networkA.broadcast, networkB.broadcast);
  if (overlapEnd < overlapStart) {
    return 0;
  }
  return overlapEnd - overlapStart + 1;
}

// Design agent: Validates if the input matches any of the supported formats:
// - ipv4
// - ipv4/cidr
// - ipv4/fullmask
// - cidr
// - fullmask
export function isValidInputFormat(input) {
  const trimmed = input.trim();
  if (!trimmed) return false;

  // Check IPv4/CIDR
  if (parseCidr(trimmed)) return true;

  // Check IPv4 (also covers fullmask as standalone IP)
  if (ipv4ToInt(trimmed) !== null) return true;

  // Check IPv4/FullMask (e.g., 192.168.1.1/255.255.255.0)
  const parts = trimmed.split('/');
  if (parts.length === 2) {
    if (ipv4ToInt(parts[0]) !== null && ipv4ToInt(parts[1]) !== null) {
      return true;
    }
  }

  // Check CIDR only (e.g., /24)
  if (trimmed.startsWith('/')) {
    const prefix = Number(trimmed.slice(1));
    return !Number.isNaN(prefix) && prefix >= 0 && prefix <= 32;
  }

  // Check IP Range (e.g., IP - IP or IP/CIDR - IP/CIDR)
  if (isValidRangeFormat(trimmed)) {
    return true;
  }

  return false;
}

// Developer agent: Identifies if an integer represents a valid subnet mask and returns its prefix length.
export function getPrefixFromMask(maskInt) {
  for (let i = 0; i <= 32; i += 1) {
    const mask = i === 0 ? 0 : (~0 << (32 - i)) >>> 0;
    if (mask === maskInt) {
      return i;
    }
  }
  return null;
}

// Developer agent: Generates a complete reference table of all 33 valid subnet masks in descending order.
export function getAllSubnetMasks() {
  const masks = [];
  for (let i = 32; i >= 0; i -= 1) {
    const maskInt = i === 0 ? 0 : (~0 << (32 - i)) >>> 0;
    masks.push({
      cidr: i,
      mask: intToIpv4(maskInt),
      wildcard: intToIpv4((~maskInt) >>> 0),
    });
  }
  return masks;
}

// Design agent: Parses a range string in format "IP - IP" or "IP/CIDR - IP/CIDR".
// Developer agent: Automatically swaps start/end if provided in reverse order.
export function parseRange(input) {
  if (typeof input !== 'string') {
    return null;
  }
  const trimmed = input.trim();

  // Split on dash with optional whitespace
  const parts = trimmed.split(/\s*-\s*/);
  if (parts.length !== 2) {
    return null;
  }

  const [startPart, endPart] = parts;

  // Parse start - can be IP or IP/CIDR
  let startIp = null;
  let startPrefix = null;
  let startNetwork = null;
  let startBroadcast = null;

  const startCidr = parseCidr(startPart);
  if (startCidr) {
    startIp = startCidr.ip;
    startPrefix = startCidr.prefix;
    startNetwork = startCidr.network;
    startBroadcast = startCidr.broadcast;
  } else {
    const startInt = ipv4ToInt(startPart.trim());
    if (startInt === null) {
      return null;
    }
    startIp = startPart.trim();
    startPrefix = null;
    startNetwork = null;
    startBroadcast = null;
  }

  // Parse end - can be IP or IP/CIDR
  let endIp = null;
  let endPrefix = null;
  let endNetwork = null;
  let endBroadcast = null;

  const endCidr = parseCidr(endPart);
  if (endCidr) {
    endIp = endCidr.ip;
    endPrefix = endCidr.prefix;
    endNetwork = endCidr.network;
    endBroadcast = endCidr.broadcast;
  } else {
    const endInt = ipv4ToInt(endPart.trim());
    if (endInt === null) {
      return null;
    }
    endIp = endPart.trim();
    endPrefix = null;
    endNetwork = null;
    endBroadcast = null;
  }

  // Convert to integers for comparison
  const startInt = ipv4ToInt(startIp);
  const endInt = ipv4ToInt(endIp);

  // Determine actual visual range bounds (for the progress bar visualization)
  // For CIDR, we use network as start and broadcast as end
  let rangeStartInt = startPrefix !== null ? startNetwork : startInt;
  let rangeEndInt = endPrefix !== null ? endBroadcast : endInt;

  // Auto-swap visual range if reversed
  let wasSwapped = false;
  if (rangeStartInt > rangeEndInt) {
    [rangeStartInt, rangeEndInt] = [rangeEndInt, rangeStartInt];
    wasSwapped = true;
  }

  // Design agent: Calculate total addresses based on the ACTUAL IPs entered (not network/broadcast)
  // This gives the user the count between their specified addresses
  let userStartInt = startInt;
  let userEndInt = endInt;

  // Auto-swap user IPs if reversed
  if (userStartInt > userEndInt) {
    [userStartInt, userEndInt] = [userEndInt, userStartInt];
  }

  // Total addresses between the user's specified IPs (inclusive of both bounds)
  const totalAddresses = userEndInt - userStartInt + 1;

  return {
    startIp,
    endIp,
    startPrefix,
    endPrefix,
    startNetwork,
    startBroadcast,
    endNetwork,
    endBroadcast,
    rangeStartInt,
    rangeEndInt,
    rangeStartIp: intToIpv4(rangeStartInt),
    rangeEndIp: intToIpv4(rangeEndInt),
    // User-specified IP integers for display
    userStartInt,
    userEndInt,
    userStartIp: intToIpv4(userStartInt),
    userEndIp: intToIpv4(userEndInt),
    totalAddresses,
    wasSwapped,
    // For simple range without CIDR
    hasStartCidr: startPrefix !== null,
    hasEndCidr: endPrefix !== null,
  };
}

// Design agent: Validates if input matches range format (IP - IP or IP/CIDR - IP/CIDR).
export function isValidRangeFormat(input) {
  return Boolean(parseRange(input));
}

// ---------------------------------------------------------------------------
// Spec 004 — Route summary explorer (src/components/OverallSummaryCard.js)
// ---------------------------------------------------------------------------

// Design agent: Finds the minimal supernet (network + prefix) that covers every entry in the
// list, i.e. the tightest possible route summary an admin could announce for the whole group.
// Developer agent: Reduces the group to its extreme network/broadcast bounds, then reuses
// commonPrefixLength — the same building block the route summary badge already relied on —
// so the two stay mathematically identical.
export function coveringPrefix(parsedList) {
  if (!Array.isArray(parsedList) || parsedList.length === 0) {
    return null;
  }
  let minNetwork = Infinity;
  let maxBroadcast = -Infinity;
  parsedList.forEach((parsed) => {
    minNetwork = Math.min(minNetwork, parsed.network);
    maxBroadcast = Math.max(maxBroadcast, parsed.broadcast);
  });
  const prefix = commonPrefixLength(minNetwork, maxBroadcast);
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
  const network = (minNetwork & mask) >>> 0;
  return { prefix, network };
}

// Design agent: Measures how much a single entry "costs" the aggregate summary — the prefix the
// group would need without it — so the impact list can point out which entry is the outlier.
// Developer agent: Filters the entry out by its position in the list and delegates back to
// coveringPrefix; when only one entry remains it naturally collapses to that entry's own prefix
// (a bare host address collapses to /32), which is exactly the "delta vs a /32" behaviour the
// spec calls for once only two entries are present.
export function summaryWithout(parsedList, excludeIndex) {
  if (!Array.isArray(parsedList)) {
    return null;
  }
  const remaining = parsedList.filter((_, index) => index !== excludeIndex);
  return coveringPrefix(remaining);
}

// ---------------------------------------------------------------------------
// Spec 005 — Overlap origin (src/components/OverallSummaryCard.js, IpInspector.js)
// ---------------------------------------------------------------------------

// Design agent: Names every overlapping pair of entries (or flags an exact duplicate) so the UI
// can point straight at the conflicting fields instead of leaving the admin to guess which two
// of many entries actually collide.
// Developer agent: Accepts a list indexed exactly like the visible entries (nulls for empty/
// invalid rows are fine and simply skipped), so the returned `a`/`b` positions already match the
// 1-based entry numbers shown to the user (entryNumber = index + 1). Reuses doNetworksOverlap and
// getOverlapSize so the totals stay identical to the existing aggregate counters.
export function findOverlaps(parsedList) {
  const overlaps = [];
  if (!Array.isArray(parsedList)) {
    return overlaps;
  }
  for (let i = 0; i < parsedList.length; i += 1) {
    const a = parsedList[i];
    if (!a) continue;
    for (let j = i + 1; j < parsedList.length; j += 1) {
      const b = parsedList[j];
      if (!b) continue;
      if (doNetworksOverlap(a, b)) {
        const isDuplicate = a.network === b.network && a.broadcast === b.broadcast;
        overlaps.push({
          a: i,
          b: j,
          shared: getOverlapSize(a, b),
          isDuplicate,
        });
      }
    }
  }
  return overlaps;
}

// ---------------------------------------------------------------------------
// Spec 006 — Longest prefix match in "belong" mode (src/components/IpInspector.js)
// ---------------------------------------------------------------------------

// Design agent: Mirrors what a real router does when several summarised networks all contain the
// same address: it never uses "every match", it forwards using the single most specific
// (longest-prefix) route. This helper exposes that decision so the UI can highlight it.
// Developer agent: Scans the candidate networks once, excluding /32 entries the same way
// isIpInSubnet does (a /32 cannot "contain" a distinct address), and tracks every network that
// ties for the longest prefix so duplicate-prefix matches can all be marked "best".
export function longestPrefixMatch(ipInt, networks) {
  let bestPrefix = -1;
  const bestIndices = [];
  if (!Array.isArray(networks) || ipInt === null || typeof ipInt !== 'number') {
    return { bestPrefix, bestIndices };
  }
  networks.forEach((network, index) => {
    if (!network || network.prefix === 32) {
      return;
    }
    if (ipInt < network.network || ipInt > network.broadcast) {
      return;
    }
    if (network.prefix > bestPrefix) {
      bestPrefix = network.prefix;
      bestIndices.length = 0;
      bestIndices.push(index);
    } else if (network.prefix === bestPrefix) {
      bestIndices.push(index);
    }
  });
  return { bestPrefix, bestIndices };
}
